import { t } from "@helpers/translate";
import classNames from "classnames";
import React, {
  ButtonHTMLAttributes,
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button as BaseButton } from "../Button";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isOpen?: boolean;
}

const Button: FC<ButtonProps> = ({ className, isOpen, ...props }) => {
  const classes = classNames(
    "relative flex mx-auto mt-4 px-20 text-base outline-none focus:outline-none",
    className
  );

  return (
    <span className="block pb-2">
      {!isOpen && (
        <span
          className="block absolute bottom-0 right-0 left-0 h-28"
          style={{ background: "rgba(255, 255, 255, 0.7)" }}
        />
      )}
      <BaseButton
        rounded
        variant="default-outline"
        className={classes}
        size="sm"
        {...props}
      />
    </span>
  );
};

interface ShowMoreProps extends HTMLAttributes<HTMLDivElement> {
  height?: number;
  children?: ReactNode;
  moreText?: string;
  lessText?: string;
}

export const ShowMore: FC<ShowMoreProps> = ({
  className,
  children,
  height = 300,
  moreText = t({ id: "showMore.button.more.generic", message: "Read more" }),
  lessText = t({ id: "showMore.button.less.generic", message: "Read less" }),
  ...props
}) => {
  const classes = classNames("w-full relative", className);

  const [isOpen, setIsOpen] = useState(false);
  const [showMoreBtn, setShowMoreBtn] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      // If content overflows
      setShowMoreBtn(ref.current.offsetHeight < ref.current.scrollHeight);
    }
  }, []);

  return (
    <div className={classes} {...props}>
      <span
        ref={ref}
        style={{ maxHeight: !isOpen ? height : "100%" }}
        className="block overflow-y-hidden"
      >
        {children}
      </span>
      {showMoreBtn && (
        <Button isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
          {!isOpen ? moreText : lessText}
        </Button>
      )}
    </div>
  );
};
